import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface Placeholder {
  label: string;
  url: string;
}

interface LicenseData {
  version: string;
  language: string;
  template: string;
  placeholders: {
    [key: string]: Placeholder;
  };
}

interface ApiResponse {
  isValid: boolean;
  errorCode: number;
  errorMessage: string | null;
  payLoad: LicenseData;
  method: string;
}

interface LicenseContextType {
  licenseTemplate: string;
  placeholders: { [key: string]: Placeholder };
  loading: boolean;
  error: string | null;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

interface LicenseProviderProps {
  children: ReactNode;
  language: string;
}

export const LicenseProvider: React.FC<LicenseProviderProps> = ({ children, language }) => {
  const [licenseTemplate, setLicenseTemplate] = useState<string>('Loading terms...');
  const [placeholders, setPlaceholders] = useState<{ [key: string]: Placeholder }>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLicenseTerms = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const lang = language === 'en' ? 'en' : 'he';
        const response = await fetch(
          `https://connect.lupa.co.il/V2/api/entry.aspx?method=getlicene&lang=${lang}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        
        if (data.isValid && data.payLoad) {
          setLicenseTemplate(data.payLoad.template);
          setPlaceholders(data.payLoad.placeholders || {});
        } else {
          throw new Error(data.errorMessage || 'Failed to fetch license terms');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        // Fallback to default template in case of error
        setLicenseTemplate('I agree to the {terms}');
        setPlaceholders({
          terms: {
            label: 'Terms and Privacy Policy',
            url: 'https://www.lupa.co.il/license/'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLicenseTerms();
  }, [language]);

  const value = {
    licenseTemplate,
    placeholders,
    loading,
    error
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicenseContext = (): LicenseContextType => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicenseContext must be used within a LicenseProvider');
  }
  return context;
};